import type { CSSProperties } from 'react';
import { isComplete, serviceClass } from '../../data/domain';
import { useJobs } from '../../data/hooks';
import { longDate, minutesOf, pad, plural, timeLabel } from '../../lib/format';
import { Icon } from '../../ui/Icon';
import { Modal } from '../../ui/Modal';
import { useUI } from '../../ui/UIProvider';

const SLOT = 30;

/** Hourly day board: jobs sized by duration in 30-minute rows, overlapping jobs side by side in lanes. */
export function DayScheduleModal({ date }: { date: string }) {
  const jobs = useJobs();
  const ui = useUI();
  const entries = jobs.filter((job) => job.date === date)
    .map((job) => ({ job, start: minutesOf(job.startTime), duration: Math.max(SLOT, Number(job.duration) || 60), lane: 0 }))
    .sort((a, b) => a.start - b.start);
  const laneEnds: number[] = [];
  for (const entry of entries) {
    const free = laneEnds.findIndex((end) => end <= entry.start);
    entry.lane = free === -1 ? laneEnds.length : free;
    laneEnds[entry.lane] = entry.start + entry.duration;
  }
  // The board starts at 7 AM, or earlier when a job starts before then.
  const dayStart = Math.min(7 * 60, ...entries.map((entry) => Math.floor(entry.start / 60) * 60));
  const slotCount = Math.max(24, ...entries.map((entry) => Math.ceil((entry.start + entry.duration - dayStart) / SLOT)));
  const style = { '--duration-slots': String(slotCount), '--duration-lanes': String(Math.max(1, laneEnds.length)) } as CSSProperties;
  const createAt = (time?: string) => ui.open('jobEditor', { mode: 'new', date, time });

  return (
    <Modal id="scheduleDayModal" size="lg" className="board-modal" onClose={() => ui.close('daySchedule')} labelledBy="dayScheduleTitle">
      <header className="modal-header modal-header-actions">
        <div><p className="eyebrow">Day board</p><h2 id="dayScheduleTitle">{longDate(date)}</h2><p className="form-help">{plural(entries.length, 'scheduled service')} · select an empty slot to book a job at that time</p></div>
        <button className="btn btn-primary" id="addDayJob" type="button" onClick={() => createAt()}><Icon name="plus" size={16} />New job</button>
      </header>
      <div className="day-board" id="hourTimeline" style={style}>
        {Array.from({ length: slotCount }, (_, index) => {
          const minutes = dayStart + index * SLOT;
          const value = `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
          return [
            minutes % 60 === 0 ? <span key={`label-${index}`} className="duration-label" style={{ gridRow: index + 1 }}>{timeLabel(value)}</span> : null,
            <button key={`slot-${index}`} type="button" className="duration-slot" style={{ gridRow: index + 1 }} aria-label={`Add a job at ${timeLabel(value)}`} onClick={() => createAt(value)} />
          ];
        })}
        {entries.map(({ job, start, duration, lane }) => {
          const complete = isComplete(job);
          return (
            <button key={job.id} className={`job-event ${serviceClass(job)}${job.cancelled ? ' cancelled' : ''}${complete ? ' completed' : ''}`}
              style={{ gridColumn: lane + 2, gridRow: `${Math.floor((start - dayStart) / SLOT) + 1} / span ${Math.max(1, Math.ceil(duration / SLOT))}` }}
              onClick={() => ui.open('jobEditor', { mode: 'edit', jobId: job.id })}>
              <b><time>{timeLabel(job.startTime)}</time> {job.serviceCode}{job.cancelled ? ' · Cancelled' : complete ? ' · Completed' : ''}</b>
              <small>{job.detail || job.serviceCode} · {job.clientName} · {job.techName} · {job.duration} min</small>
            </button>
          );
        })}
      </div>
    </Modal>
  );
}
